import { normalizeGeofence } from '../../utils/geofenceUtils';
import { isInsuranceAddressed, normalizeWizardInsurance } from '../../utils/insuranceHelpers';
import { isStructuredInstructions } from '../../utils/parkingInstructions';

export const WIZARD_STEP_COUNT = 7;

// PP-403 — keys the wizard knows how to manage inside `confirmedFields`,
// each with a runtime type predicate. Used to strip deprecated/unknown
// keys at hydration so the wizard never echoes a legacy field (e.g.
// `hours`, removed in PP-343) back to PUT /listings/:id. Ishara C3:
// also drop keys whose stored values are the wrong shape — a string
// `amenities` from an old client write would otherwise flow into React
// state and break the Step 3 completion check / Step 3 amenity list.
const isString = (v) => typeof v === 'string';
const isStringArray = (v) => Array.isArray(v) && v.every((x) => typeof x === 'string');
// availability is the legacy `{ allDay, schedule }` object OR a bare
// schedule array — both are acceptable to the server validator and to
// the Step 3 normalizer.
const isAvailability = (v) => (
    v != null
    && (Array.isArray(v) || (typeof v === 'object' && !Array.isArray(v)))
);
// instructions is either a legacy plain string or the structured
// per-approach-road object. Accept both shapes so an accepted AI
// suggestion survives re-hydration instead of being silently dropped.
const isInstructions = (v) => isString(v) || isStructuredInstructions(v);

const WIZARD_CONFIRMED_FIELDS_GUARDS = {
    name: isString,
    type: isString,
    instructions: isInstructions,
    entrance: isString,
    surface: isString,
    amenities: isStringArray,
    availability: isAvailability,
};

export const WIZARD_CONFIRMED_FIELDS_KEYS = Object.keys(WIZARD_CONFIRMED_FIELDS_GUARDS);

export function pickWizardConfirmedFields(blob) {
    if (!blob || typeof blob !== 'object' || Array.isArray(blob)) return {};
    const out = {};
    for (const k of WIZARD_CONFIRMED_FIELDS_KEYS) {
        if (!(k in blob)) continue;
        // null / undefined are valid "field not set yet" sentinels in
        // confirmedFields — preserve them so the wizard's completion
        // check still distinguishes "key absent" (legacy) from "key
        // explicitly null" (host saw it, didn't pick a value).
        if (blob[k] === null || blob[k] === undefined) {
            out[k] = blob[k];
            continue;
        }
        if (!WIZARD_CONFIRMED_FIELDS_GUARDS[k](blob[k])) continue;
        out[k] = blob[k];
    }
    return out;
}

export function deriveWizardCompletedSteps({
    addressData,
    geofence,
    confirmedFields,
    photos,
    pricing,
}) {
    const done = [];
    if (addressData && (
        addressData.formatted_address
        || addressData.formattedAddress
        || (typeof addressData.lat === 'number' && typeof addressData.lng === 'number')
    )) {
        done.push(1);
    }
    const normalizedGeofence = normalizeGeofence(geofence);
    if (normalizedGeofence.some((poly) => poly.length >= 3)) {
        done.push(2);
    }
    if (confirmedFields && typeof confirmedFields === 'object' && Object.keys(confirmedFields).length > 0) {
        done.push(3);
    }
    if (Array.isArray(photos) && photos.length > 0) {
        done.push(4);
    }
    if (pricing && pricing.hourlyRate != null && pricing.maxSpots != null) {
        done.push(5);
    }
    return done;
}

// Form defaults to No visually, so only treat step 6 as complete when the user actually saved an answer.
function isInsuranceExplicitlyAnswered(rawInsurance) {
    if (!rawInsurance || typeof rawInsurance !== 'object') return false;
    return rawInsurance.required === true || rawInsurance.required === false;
}

export function deriveWizardCompletedStepsFromListing(listing) {
    if (!listing) return [];
    const completed = deriveWizardCompletedSteps({
        addressData: listing.addressData,
        geofence: listing.geofence,
        confirmedFields: listing.confirmedFields,
        photos: listing.photos,
        pricing: listing.pricing,
    });
    if (
        isInsuranceExplicitlyAnswered(listing.insurance)
        && isInsuranceAddressed(normalizeWizardInsurance(listing.insurance))
    ) {
        completed.push(6);
    }
    return completed;
}

/** Resume SPW on the highest step the host has already completed (not the next gap). */
export function resolveWizardResumeStep(listing) {
    const completed = deriveWizardCompletedStepsFromListing(listing);
    if (completed.length === 0) return 1;
    return Math.max(...completed);
}

export function getFirstIncompleteWizardStep(completedStepIndexes, totalSteps = WIZARD_STEP_COUNT) {
    for (let i = 1; i <= totalSteps; i += 1) {
        if (!completedStepIndexes.includes(i)) return i;
    }
    return totalSteps + 1;
}

/**
 * Apply a `GET /listings/:id` response into the wizard context via the
 * supplied setters. Pure — no React, no axios. Lives here so the
 * GET-response → context wiring is testable without mounting the full
 * OnboardingWizardPage (and the 7 step components + auth/page contexts
 * + react-router it transitively pulls in).
 *
 * `markInsuranceVisited` is called only when the listing has a real
 * insurance answer (`required` true or false — not null/undefined),
 * which is the signal the host has actually been to Step 6 before and
 * the completion gate in OnboardingWizardPage should treat Step 6 as
 * complete in the stepper.
 *
 * @param {object|null|undefined} listing — the GET response listing.
 * @param {{
 *   setAddressData: Function, setConfirmedFields: Function,
 *   setPhotos: Function, setPricing: Function, setInsurance: Function,
 *   setGeoSpaceCount: Function, setManagers: Function, setGeofence: Function,
 *   setIngress: Function, setEgress: Function, setMaxStepReached: Function,
 *   markInsuranceVisited: Function,
 * }} setters — wizard-context setters. Required.
 */
export function hydrateWizardFromListing(listing, setters) {
    if (!listing || typeof listing !== 'object') return;
    if (!setters || typeof setters !== 'object') return;
    if (listing.addressData) setters.setAddressData(listing.addressData);
    if (listing.confirmedFields) {
        // Strip legacy/unknown keys so re-saving a pre-PP-343 listing
        // doesn't carry deprecated `hours` (or any future-removed key)
        // through to the server validator.
        setters.setConfirmedFields(pickWizardConfirmedFields(listing.confirmedFields));
    }
    setters.setPhotos(listing.photos || []);
    setters.setPricing(listing.pricing || { hourlyRate: null, maxSpots: null });
    setters.setInsurance(normalizeWizardInsurance(listing.insurance));
    if (listing.insurance && (listing.insurance.required === true || listing.insurance.required === false)) {
        setters.markInsuranceVisited();
    }
    setters.setGeoSpaceCount(listing.geoSpaceCount ?? null);
    setters.setManagers(listing.managers || []);
    if (listing.geofence) {
        setters.setGeofence(listing.geofence);
    }
    setters.setIngress(Array.isArray(listing.ingress) ? listing.ingress : []);
    setters.setEgress(Array.isArray(listing.egress) ? listing.egress : []);
    setters.setMaxStepReached(resolveWizardResumeStep(listing));
}
